// errorDiagnosis — pattern-matches error_log entries to produce human-readable
// diagnoses with actionable fix suggestions for super admins.

import { describeUserAgent } from './errorIdentity';

export interface ErrorDiagnosis {
  title: string;
  description: string;
  suggestedFix: string;
  severity: 'warning' | 'error' | 'critical';
  /** The cause involves the session, token or device clock, so the auth diagnostics belong on the report. */
  authRelated?: boolean;
}

interface AuthDiagnostics {
  sessionExists?: boolean;
  tokenExpiresAt?: string | null;
  secondsToExpiry?: number | null;
  clockSkewMs?: number | null;
  serverNowEstimate?: string | null;
  userAgent?: string | null;
}

export interface ErrorLogEntry {
  error_message: string;
  http_status?: number | null;
  query_name?: string | null;
  error_type?: string | null;
  stack_trace?: string | null;
  page?: string | null;
  auth_diagnostics?: AuthDiagnostics | null;
  source?: string | null;
  occurrence_count?: number | null;
}

interface Rule {
  test: (e: ErrorLogEntry) => boolean;
  diagnose: (e: ErrorLogEntry) => ErrorDiagnosis;
}

const msg = (e: ErrorLogEntry) => (e.error_message ?? '').toLowerCase();
const stack = (e: ErrorLogEntry) => (e.stack_trace ?? '').toLowerCase();
const combined = (e: ErrorLogEntry) => `${msg(e)} ${stack(e)}`;
const trimmed = (v: string | null | undefined) => (v ?? '').trim();

/** Who was there: signed in or not, and what browser or bot sent it. */
const whoWasThere = (e: ErrorLogEntry): string => {
  const ad = e.auth_diagnostics;
  const session = ad?.sessionExists === true ? 'The person was signed in.'
    : ad?.sessionExists === false ? 'No one was signed in; the browser was on a public page.'
    : '';
  const ua = ad?.userAgent ? ` Sent by ${describeUserAgent(ad.userAgent)}.` : '';
  return `${session}${ua}`.trim();
};

/** Whether any frame of the stack comes from our own bundle. */
const fromOwnBundle = (e: ErrorLogEntry) => /\/assets\//.test(e.stack_trace ?? '');

const rules: Rule[] = [
  // ── The app's own delete / deactivate guards ───────────────────────
  // Raised on purpose by is_protected_account() checks in admin_delete_user,
  // the protect_root_members trigger, the deactivation functions, and
  // admin_delete_org. The message names the action; the cause is always the
  // same three-clause test.
  {
    test: e => /protected account:/i.test(msg(e)),
    diagnose: e => {
      if (/platform root org/i.test(msg(e))) {
        return {
          title: 'Refused: Platform Root Org',
          description:
            'The database refused to delete the Tactical Tax root org. admin_delete_org never deletes the org marked is_platform_root. Nothing was deleted.',
          suggestedFix: 'The root org is not deletable from the app by design.',
          severity: 'warning',
        };
      }
      if (/deactivate themselves/i.test(msg(e))) {
        return {
          title: 'Refused: Platform Staff Self-Deactivation',
          description:
            'A platform staff member tried Deactivate my account. deactivate_self refuses anyone is_protected_account() names. Nothing was changed.',
          suggestedFix: 'Platform staff accounts are not deactivated from Settings.',
          severity: 'warning',
        };
      }
      const action = /deactivated or deleted/i.test(msg(e)) ? 'deactivate or delete a branch holding'
        : /deactivated/i.test(msg(e)) ? 'deactivate'
        : 'delete';
      return {
        title: 'Refused: Protected Account',
        description:
          `The database refused to ${action} a protected account. is_protected_account() is true when the person holds super_admin or platform_admin, when their profile's org is the Tactical Tax root org, or when they own the root org. The check refuses every caller, super admin included. Nothing was changed.`,
        suggestedFix:
          'To see which of the three makes this person protected, look them up in user_roles and check whether profiles.org_id is the org with is_platform_root = true.',
        severity: 'warning',
      };
    },
  },

  // ── Cross-tab auth lock ────────────────────────────────────────────
  // These MUST stay ahead of the generic rules: the raw steal message arrives
  // with error_type 'crash' via unhandledrejection and would otherwise render
  // as "Application Crash".
  {
    // Emitted by errorLogger's trackLockSteal() when steals repeat — the one
    // lock condition that is a genuine malfunction.
    test: e => /lock-steal storm/i.test(msg(e)),
    diagnose: () => ({
      authRelated: true,
      title: 'Auth Lock Contention Storm',
      description:
        'The cross-tab auth refresh lock was force-recovered repeatedly within minutes. A single recovery is the designed self-healing path (a suspended tab held the lock; another tab took it after 5s) and is never logged — repetition means something keeps grabbing the lock and hanging, so the recovery is spinning instead of the lock working.',
      suggestedFix:
        "Ask the user how many tabs they have open and whether one is frozen — closing the hung tab stops the storm. If it recurs with a single tab, check for a wedged service worker or extension interfering with the Web Locks API, and look at auth diagnostics for clock skew or refresh failures around the same time.",
      severity: 'critical',
    }),
  },
  {
    // Raw steal AbortError — new clients suppress these before logging, so a row
    // like this comes from a pre-suppression bundle or the historical backlog.
    // Chrome says "Lock broken by another request"; Firefox says only "The
    // operation was aborted" (Andrew, 2026-11-03, read as Unknown Error until
    // then). Nothing else in the app rejects with that wording.
    test: e => /lock broken by another request|the operation was aborted/i.test(msg(e)),
    diagnose: () => ({
      authRelated: true,
      title: 'Cross-Tab Lock Recovery (Benign)',
      description:
        "One tab held the auth refresh lock too long (usually suspended by the browser), so another tab force-recovered it after 5 seconds. The losing tab's in-flight queries rejected once with this message and immediately retried against the healthy session. Nobody was signed out; nothing was lost. This is the lock's designed recovery, not a failure.",
      suggestedFix:
        'Nothing to fix — this self-heals. Current clients no longer log isolated recoveries at all (only repeated ones, as "Auth Lock Contention Storm"), in Chrome\'s wording or Firefox\'s ("The operation was aborted"), so this row is from an older bundle or the backlog. Safe to acknowledge.',
      severity: 'warning',
    }),
  },

  // ── Content Security Policy ────────────────────────────────────────
  // vercel.json sends `script-src 'self'`. A refusal to run code from a string
  // is the policy stopping a script, and whose script it was is the whole
  // question: our bundle never evals, so a stack with no /assets/ frame is
  // automation (Puppeteer/Playwright's poller), an extension or a scanner.
  // Current clients drop that case before logging; a row here is from an
  // older bundle or the backlog. A stack WITH an /assets/ frame is our own
  // dependency evaling, which the policy will keep breaking until it is fixed.
  {
    test: e => /content security policy/i.test(msg(e)),
    diagnose: e => {
      const evalRefusal = /refused to evaluate a string/i.test(msg(e));
      if (evalRefusal && !fromOwnBundle(e)) {
        return {
          title: 'Foreign Script Blocked by CSP',
          description:
            `A script that is not ours tried to run code from a string and the Content Security Policy refused it. The stack has no frame from our bundle: it is browser automation (a headless browser's wait-for-condition poller), a browser extension, or a scanner probing the page. The policy did its job; nothing of ours ran wrong and nothing was reached. ${whoWasThere(e)}`,
          suggestedFix:
            'Nothing to fix. Acknowledge it. Current clients no longer log this case at all, so a new row means an older bundle in someone\'s tab. If the same browser keeps coming back signed out, it is a crawler or scanner; a signed-in person seeing it has an extension that injects code.',
          severity: 'warning',
        };
      }
      return {
        title: evalRefusal ? 'Our Code Evals Under CSP' : 'Blocked by Content Security Policy',
        description:
          `${evalRefusal ? 'Code from our own bundle asked the browser to run a string as JavaScript' : 'The browser refused a load or connection'}, and the Content Security Policy in vercel.json refused it. This breaks whatever feature needed it, for everyone, on every page that runs this code. ${whoWasThere(e)}`,
        suggestedFix:
          `Find the /assets/ frame in the stack and the dependency behind it. Either replace the dependency with one that does not need ${evalRefusal ? "'unsafe-eval'" : 'that origin'}, or deliberately widen the policy and say why in the PR. The mock-production smoke run enforces the same policy, so add a check that visits the page.${e.page ? ` Page: ${e.page}.` : ''}`,
        severity: 'critical',
      };
    },
  },

  // ── Permission / Auth ──────────────────────────────────────────────
  {
    test: e =>
      /permission denied|forbidden|42501/i.test(msg(e)) ||
      e.http_status === 403,
    diagnose: e => {
      const ad = e.auth_diagnostics;
      if (ad && !ad.sessionExists) {
        return {
          authRelated: true,
          title: 'Permission Denied (No Session)',
          description: "This looks like a permissions error, but the user had no active session. The request went out without a JWT, so the server treated it as anonymous and RLS blocked it. This is an auth failure, not a role problem.",
          suggestedFix: 'The user needs to sign back in. Check for other auth errors around the same time — this is usually part of a session collapse, not a missing role.',
          severity: 'error',
        };
      }
      const skewMs = ad?.clockSkewMs;
      if (skewMs != null && Math.abs(skewMs) >= 30 * 60_000) {
        const skewDesc = Math.abs(skewMs) >= 3_600_000 ? `${Math.round(Math.abs(skewMs) / 3_600_000)} hour(s)` : `${Math.round(Math.abs(skewMs) / 60_000)} minutes`;
        return {
          authRelated: true,
          title: 'Permission Denied (Clock Skew)',
          description: `The user's device clock is off by ~${skewDesc}. This caused the token refresh to misfire, so the request went out with a dead JWT. The server correctly rejected it, but the root cause is the clock, not missing permissions.`,
          suggestedFix: 'Tell the user to set their date & time to automatic. This is the same pattern as Andrew\'s Boca issue.',
          severity: 'critical',
        };
      }
      return {
        title: 'Permission Denied',
        description: 'The user attempted an action their role does not allow. The database rejected the query due to insufficient privileges or an RLS policy.',
        suggestedFix: `Check the user's role in user_roles — they may need org_admin or org_owner. Verify RLS policies on the affected table.${e.query_name ? ` Query context: ${e.query_name}.` : ''}`,
        severity: 'error',
      };
    },
  },
  {
    test: e =>
      /row-level security|new row violates|rls/i.test(combined(e)),
    diagnose: e => ({
      title: 'RLS Policy Violation',
      description:
        "A row-level security policy blocked this operation. The user's org_id or role didn't satisfy the policy check.",
      suggestedFix: `Check the user's profile.org_id and user_roles entries. Ensure they belong to the correct org and have the required role.${e.query_name ? ` Affected query: ${e.query_name}.` : ''}`,
      severity: 'error',
    }),
  },
  {
    test: e =>
      /jwt expired|invalid claim|invalid token|token.*(expired|invalid)/i.test(msg(e)) ||
      e.http_status === 401,
    diagnose: e => {
      const ad = e.auth_diagnostics;
      const skewMs = ad?.clockSkewMs;
      const absSkew = skewMs != null ? Math.abs(skewMs) : 0;
      if (absSkew >= 30 * 60_000) {
        const skewDesc = absSkew >= 86_400_000 ? `${Math.round(absSkew / 86_400_000)} day(s)`
          : absSkew >= 3_600_000 ? `${Math.round(absSkew / 3_600_000)} hour(s)`
          : `${Math.round(absSkew / 60_000)} minutes`;
        return {
          authRelated: true,
          title: 'Session Killed by Wrong Clock',
          description: `The user's device clock is off by ~${skewDesc}. The Supabase SDK uses the local clock to schedule token refresh — a wrong clock makes it mistime renewals, so the server rejects requests as unauthenticated.`,
          suggestedFix: 'Tell the user to set their date & time to automatic. Once the clock is correct, sign back in. This is the same issue Andrew had in Boca.',
          severity: 'critical',
        };
      }
      if (ad && !ad.sessionExists) {
        return {
          authRelated: true,
          title: 'Session Gone',
          description: "The user had no active session when this error occurred. Their token was already dead — they weren't just expired, they were fully signed out.",
          suggestedFix: 'The user needs to sign in again. If this keeps happening, check if something is clearing their session (another tab signing out, clearing cookies, antivirus).',
          severity: 'error',
        };
      }
      if (ad?.secondsToExpiry != null && ad.secondsToExpiry < 0) {
        return {
          authRelated: true,
          title: 'Token Expired (Refresh Failed)',
          description: `The user's token expired ${Math.abs(Math.round(ad.secondsToExpiry / 60))} minute(s) ago and the automatic refresh didn't kick in. The refresh may have been blocked by a rate limit, network issue, or tab sleeping.`,
          suggestedFix: 'The user needs to sign back in. If recurring, check for rate-limit errors (429s) in the logs around the same time — a refresh storm may have been blocked.',
          severity: 'error',
        };
      }
      return {
        authRelated: true,
          title: 'Session Expired',
        description: "The user's JWT token expired or was invalid. Their session is no longer authenticated.",
        suggestedFix: 'The user needs to sign out and sign back in. If this happens frequently, check the Supabase Auth JWT expiry settings and token refresh configuration.',
        severity: 'warning',
      };
    },
  },

  // ── Constraint violations ──────────────────────────────────────────
  {
    test: e => /duplicate key|unique.*(constraint|violation)|23505/i.test(msg(e)),
    diagnose: e => ({
      title: 'Duplicate Record',
      description:
        'The user tried to create a record that violates a unique constraint. A record with the same key already exists.',
      suggestedFix: `Check for duplicate emails, slugs, or scenario names in the affected table.${e.query_name ? ` Query: ${e.query_name}.` : ''} The user should use a different value.`,
      severity: 'warning',
    }),
  },
  {
    test: e => /foreign key|23503/i.test(msg(e)),
    diagnose: e => ({
      title: 'Reference Error',
      description:
        'A foreign key constraint was violated — either referencing a record that doesn\'t exist, or trying to delete a record still referenced by other rows.',
      suggestedFix: `Check the cascade rules for the affected table. The referenced record may have been deleted, or dependent records need to be removed first.${e.query_name ? ` Query: ${e.query_name}.` : ''}`,
      severity: 'error',
    }),
  },
  {
    test: e => /null value|not-null|23502/i.test(msg(e)),
    diagnose: e => ({
      title: 'Missing Required Field',
      description:
        'A required database field received a null value. Either the form didn\'t send the field or the server-side logic failed to populate it.',
      suggestedFix: `Check the form validation for the affected operation and verify the column constraints.${e.query_name ? ` Query context: ${e.query_name}.` : ''}`,
      severity: 'error',
    }),
  },

  // ── Not found ──────────────────────────────────────────────────────
  {
    test: e =>
      /not found|PGRST116|no rows/i.test(msg(e)) || e.http_status === 404,
    diagnose: () => ({
      title: 'Record Not Found',
      description:
        'The requested record does not exist. It may have been deleted by another user or process, or the ID is incorrect.',
      suggestedFix:
        'The user should refresh their page to get the latest data. If persistent, check whether a cascade delete or migration removed the record.',
      severity: 'warning',
    }),
  },

  // ── Network / connectivity ─────────────────────────────────────────
  {
    test: e =>
      /failed to fetch|networkerror|econnrefused|econnreset|network/i.test(msg(e)),
    diagnose: () => ({
      title: 'Connection Error',
      description:
        'The client could not reach the Supabase backend. This is typically a network issue on the user\'s end, or the Supabase project may be down.',
      suggestedFix:
        'Check the Supabase dashboard status page. If the project is healthy, the issue is likely the user\'s internet connection, a VPN, or a firewall. Check for CORS misconfigurations if the error is recurring across multiple users.',
      severity: 'error',
    }),
  },
  {
    test: e =>
      /timeout|etimedout|57014|statement timeout/i.test(msg(e)),
    diagnose: e => ({
      title: 'Request Timeout',
      description:
        'A database query or API call took too long and was terminated. This may indicate a slow query or heavy server load.',
      suggestedFix: `Check for missing indexes on the affected table. Review the query for inefficient joins or large result sets.${e.query_name ? ` Slow query: ${e.query_name}.` : ''} Consider adding pagination or optimizing the query.`,
      severity: 'error',
    }),
  },
  {
    test: e =>
      /rate limit|too many request/i.test(msg(e)) || e.http_status === 429,
    diagnose: e => {
      const count = e.occurrence_count ?? 1;
      if (count > 10) {
        return {
          authRelated: true,
          title: 'Rate-Limited Refresh Storm',
          description: `The server returned 429 (rate limited) ${count} times. This is a token refresh storm — the SDK kept trying to refresh and the server kept rejecting it. Likely caused by a wrong device clock or a stuck refresh loop.`,
          suggestedFix: 'Check the auth diagnostics for clock skew. If the clock is off, the user needs to set date & time to automatic. If the clock is fine, check for multiple tabs or a stuck service worker hammering the token endpoint.',
          severity: 'critical',
        };
      }
      return {
        title: 'Rate Limited',
        description: 'The API rate limit was exceeded. The user (or an automated process) is making too many requests in a short period.',
        suggestedFix: 'Check if the user has automation or scripts hitting the API. If legitimate usage, consider increasing rate limits in Supabase settings or adding client-side request debouncing.',
        severity: 'warning',
      };
    },
  },

  // ── Missing DB objects ─────────────────────────────────────────────
  {
    test: e =>
      /function.*does not exist|42883|relation.*does not exist|42P01/i.test(msg(e)),
    diagnose: e => ({
      title: 'Missing Database Object',
      description:
        'A database function or table referenced in the code does not exist. A migration may not have been applied, or it was dropped.',
      suggestedFix: `Run any pending Supabase migrations. Check the migrations folder for the function/table name mentioned in the error.${e.query_name ? ` Query: ${e.query_name}.` : ''}`,
      severity: 'critical',
    }),
  },

  // ── App crashes ────────────────────────────────────────────────────
  // Three different things arrive as error_type 'crash': a promise nobody
  // caught (window unhandledrejection), a thrown error outside React
  // (window error), and a render crash the ErrorBoundary caught. They used
  // to share one "React crashed in render" diagnosis, which was wrong for
  // the first two.
  {
    test: e => e.error_type === 'crash' && /^unhandled rejection:/i.test(msg(e)),
    diagnose: e => ({
      title: 'Unhandled Promise Rejection',
      description:
        `An async operation (a fetch, a save, a dynamic import, a timer) failed and nothing caught it. React did not crash and the screen most likely stayed up; whatever that operation was meant to do did not happen. ${whoWasThere(e)}`,
      suggestedFix: `Read the message after "unhandled rejection:" for the failing operation, then the stack for the caller. If the stack has no /assets/ frame the code is not ours (an extension or automation).${e.page ? ` Page: ${e.page}.` : ''}`,
      severity: 'error',
    }),
  },
  {
    test: e => e.error_type === 'crash' && /^uncaught:/i.test(msg(e)),
    diagnose: e => ({
      title: 'Uncaught Error',
      description:
        `An error was thrown outside React's render (a click handler, a callback, a timer) and nothing caught it. The screen most likely stayed up; the action the person took did not finish. ${whoWasThere(e)}`,
      suggestedFix: `Read the stack for the handler that threw. If the stack has no /assets/ frame the code is not ours (an extension or automation).${e.page ? ` Page: ${e.page}.` : ''}`,
      severity: 'error',
    }),
  },
  {
    test: e => e.error_type === 'crash',
    diagnose: e => ({
      title: 'Application Crash',
      description:
        `A component threw during render or lifecycle, the ErrorBoundary caught it, and the person saw the error screen instead of the page. This is a code bug. ${whoWasThere(e)}`,
      suggestedFix: `Check the stack trace to identify the component that crashed. Look for the "Component stack" section to find the exact component tree.${e.page ? ` The crash occurred on page: ${e.page}.` : ''}`,
      severity: 'critical',
    }),
  },

  // ── Subscription / billing ─────────────────────────────────────────
  {
    test: e =>
      /subscription|trial.*expir|billing|payment/i.test(msg(e)),
    diagnose: () => ({
      title: 'Subscription Issue',
      description:
        "The operation was blocked due to the organization's subscription or trial status.",
      suggestedFix:
        'Check the organizations table for the user\'s org — look at subscription_status, subscription_plan, and trial_ends_at. The org may need to be reactivated or the trial extended.',
      severity: 'warning',
    }),
  },

  // ── Validation ─────────────────────────────────────────────────────
  {
    test: e => /validation|invalid.*input|invalid.*value|check constraint|23514/i.test(msg(e)),
    diagnose: e => ({
      title: 'Validation Error',
      description:
        'The input data failed a validation check — either client-side form validation or a database check constraint.',
      suggestedFix: `Review the input data logged below and check which field failed validation.${e.query_name ? ` Query context: ${e.query_name}.` : ''} Ensure the form enforces the same constraints as the database.`,
      severity: 'warning',
    }),
  },

  // ── Storage / file upload ──────────────────────────────────────────
  {
    test: e =>
      /exceeded size|bucket not found|storage.*error|file.*too large|upload.*fail/i.test(combined(e)),
    diagnose: () => ({
      title: 'Storage / Upload Error',
      description:
        'A file upload failed — either the file exceeds the size limit, the storage bucket doesn\'t exist, or the upload was rejected.',
      suggestedFix:
        'Check the Supabase Storage settings for file size limits and bucket configuration. Ensure the bucket exists and RLS policies allow uploads for the user\'s role.',
      severity: 'error',
    }),
  },

  // ── CORS ───────────────────────────────────────────────────────────
  {
    test: e =>
      /cors|access-control-allow-origin|blocked by cors/i.test(combined(e)),
    diagnose: () => ({
      title: 'CORS Misconfiguration',
      description:
        'The browser blocked a cross-origin request. The server isn\'t returning the correct Access-Control-Allow-Origin headers.',
      suggestedFix:
        'Check the Supabase project\'s API settings and any Edge Function CORS headers. Ensure the app\'s domain is in the allowed origins list. If using custom Edge Functions, verify the _shared/cors.ts helper is applied.',
      severity: 'error',
    }),
  },

  // ── Payload too large ──────────────────────────────────────────────
  {
    test: e =>
      /payload too large|request entity too large/i.test(msg(e)) || e.http_status === 413,
    diagnose: () => ({
      title: 'Payload Too Large',
      description:
        'The request body exceeded the server\'s size limit. This typically happens with large form submissions or file uploads sent as base64.',
      suggestedFix:
        'Reduce the payload size — use direct file uploads to Storage instead of embedding files in the request body. If this is a legitimate large payload, check Supabase or Edge Function body size limits.',
      severity: 'warning',
    }),
  },

  // ── Stack overflow / recursion ─────────────────────────────────────
  {
    test: e =>
      /maximum call stack|stack overflow/i.test(combined(e)),
    diagnose: e => ({
      title: 'Stack Overflow',
      description:
        'An infinite recursion or deeply nested call crashed the JavaScript runtime. This is a code bug.',
      suggestedFix: `Check the stack trace for recursive function calls or circular component renders.${e.page ? ` Occurred on page: ${e.page}.` : ''} Look for useEffect loops with missing or incorrect dependency arrays.`,
      severity: 'critical',
    }),
  },

  // ── Edge Function errors ───────────────────────────────────────────
  {
    test: e =>
      /edge function|boot error|function returned.*error|deno.*error|function invocation/i.test(combined(e)) ||
      e.http_status === 500 || e.http_status === 502 || e.http_status === 503,
    diagnose: e => ({
      title: 'Edge Function Error',
      description:
        'A Supabase Edge Function failed to execute properly. It may have crashed on boot, thrown an unhandled error, or timed out.',
      suggestedFix: `Check the Edge Function logs in the Supabase dashboard for the specific error. Common causes: missing environment variables, import errors, or unhandled exceptions.${e.query_name ? ` Function context: ${e.query_name}.` : ''}`,
      severity: 'error',
    }),
  },

  // ── Realtime / WebSocket ───────────────────────────────────────────
  {
    test: e =>
      /websocket|realtime|channel error|subscription.*error|socket.*close/i.test(combined(e)),
    diagnose: () => ({
      title: 'Realtime Connection Error',
      description:
        'The WebSocket connection to Supabase Realtime failed or was unexpectedly closed. Live data updates may not be working.',
      suggestedFix:
        'Check if Realtime is enabled on the relevant tables in Supabase. Verify the user\'s network connection is stable. If recurring, check Supabase project quotas for Realtime connections.',
      severity: 'warning',
    }),
  },

  // ── Quota / resource limits ────────────────────────────────────────
  {
    test: e =>
      /quota|out of memory|resource exhausted|disk.*full|memory.*limit/i.test(combined(e)),
    diagnose: () => ({
      title: 'Resource Limit Reached',
      description:
        'The system hit a resource quota — memory, disk, or API call limit. The Supabase project may need to be upgraded or cleaned up.',
      suggestedFix:
        'Check the Supabase dashboard for usage metrics (database size, bandwidth, Edge Function invocations). Consider upgrading the plan or cleaning up old data (e.g., acknowledged error_log entries, old scenarios).',
      severity: 'critical',
    }),
  },

  // ── Serialization / data format ────────────────────────────────────
  {
    test: e =>
      /unexpected token|invalid json|syntax error.*json|json.*parse/i.test(combined(e)),
    diagnose: e => ({
      title: 'Data Serialization Error',
      description:
        'Failed to parse JSON data — the response or stored data contains malformed JSON. This could be a corrupted record or an API returning HTML instead of JSON.',
      suggestedFix: `Check the raw data in the affected table or API response. If the error is from a Supabase query, the column may contain invalid JSON.${e.query_name ? ` Query: ${e.query_name}.` : ''} If from an Edge Function, ensure it returns proper JSON content-type.`,
      severity: 'error',
    }),
  },

  // ── Email / invite delivery ────────────────────────────────────────
  {
    test: e =>
      /email.*send|smtp|invite.*fail|resend.*error|mail.*deliver/i.test(combined(e)),
    diagnose: () => ({
      title: 'Email Delivery Failure',
      description:
        'An email (invitation, password reset, or notification) failed to send. The email service may be misconfigured or rate-limited.',
      suggestedFix:
        'Check the Supabase Auth email settings and any custom SMTP configuration. If using Resend or another provider, verify the API key is valid and the sending domain is verified. Check for rate limits on the email service.',
      severity: 'error',
    }),
  },
];

/**
 * Diagnose an error_log entry and return a human-readable title, description,
 * and suggested fix for the super admin.
 */

export function diagnoseError(entry: ErrorLogEntry): ErrorDiagnosis {
  for (const rule of rules) {
    if (rule.test(entry)) return rule.diagnose(entry);
  }

  const where = [
    trimmed(entry.query_name) && `query ${entry.query_name}`,
    entry.http_status && `HTTP ${entry.http_status}`,
    trimmed(entry.page) && `on ${entry.page}`,
  ].filter(Boolean).join(', ');
  return {
    title: 'Unknown Error',
    description: `No diagnosis rule matches this message, so the cause is not identified.${where ? ` It came from ${where}.` : ''}`,
    suggestedFix: '',
    severity: 'error',
  };
}

/** Pages a browser is on without a session: no session there is normal, not broken. */
const PUBLIC_PAGE = /^\/(auth|reset-password|invite)(\/|$)/;

/** A session broken badly enough to explain a failure on its own. */
function sessionBroken(ad: AuthDiagnostics, page: string | null | undefined): boolean {
  // Drew, 2026-11-04: the block showed on a signed-out browser's CSP row on
  // /auth, where nobody is ever signed in, and read as a session problem.
  if (ad.sessionExists === false && !PUBLIC_PAGE.test(page ?? '')) return true;
  if (ad.secondsToExpiry != null && ad.secondsToExpiry < 0) return true;
  if (ad.clockSkewMs != null && Math.abs(ad.clockSkewMs) >= 30 * 60_000) return true;
  return false;
}

/**
 * The auth diagnostics to show on a report, or null. Every report carries
 * them, but they belong on screen only when the diagnosis is about the
 * session, or when the session itself was broken at the time.
 */
export function authDiagnosticsFor(entry: ErrorLogEntry, diagnosis: ErrorDiagnosis): AuthDiagnostics | null {
  const ad = entry.auth_diagnostics;
  if (!ad) return null;
  return diagnosis.authRelated || sessionBroken(ad, entry.page) ? ad : null;
}
